import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agenciesControllerAssignSubscriptionV1,
  agenciesControllerCreateV1,
  agenciesControllerFindAllV1,
  agenciesControllerFindOneV1,
  agenciesControllerRemoveSubscriptionV1,
  agenciesControllerResendRootInviteV1,
} from '@/api/generated/agencies/agencies';
import type {
  AgenciesControllerFindAllV1Params,
  AssignSubscriptionDto,
  CreateAgencyDto,
} from '@/api/generated/model';

/**
 * Query key factory — every module defines one.
 *
 * The hierarchy matters: invalidating `agencyKeys.lists()` after creating an
 * agency refreshes every filtered/sorted/paginated list without discarding
 * cached detail pages.
 */
export const agencyKeys = {
  all: ['agencies'] as const,
  lists: () => [...agencyKeys.all, 'list'] as const,
  list: (params: AgenciesControllerFindAllV1Params) => [...agencyKeys.lists(), params] as const,
  details: () => [...agencyKeys.all, 'detail'] as const,
  detail: (id: string) => [...agencyKeys.details(), id] as const,
};

export function useAgenciesQuery(params: AgenciesControllerFindAllV1Params) {
  return useQuery({
    queryKey: agencyKeys.list(params),
    queryFn: () => agenciesControllerFindAllV1(params),
    // Keeps the previous page on screen while the next loads, so the table
    // doesn't flash empty on every pagination click.
    placeholderData: (previous) => previous,
  });
}

export function useAgencyQuery(id: string) {
  return useQuery({
    queryKey: agencyKeys.detail(id),
    queryFn: () => agenciesControllerFindOneV1(id),
  });
}

/**
 * ★ REFERENCE MUTATION ★
 *
 * The shape every mutation in this codebase follows. Three things it encodes:
 *
 *   1. No retry. At the HTTP layer a create is indistinguishable from a retry,
 *      and creating an agency twice sends two activation emails to a real
 *      person. (`retry: false` is already the queryClient default; it is not
 *      repeated here.)
 *   2. Invalidate through the key factory, never with a hand-written key
 *      string. `lists()` refreshes every cached page and filter combination
 *      while leaving detail pages alone.
 *   3. Errors are NOT swallowed here. The caller decides what to do with them,
 *      because only the caller knows whether a field error belongs on a form
 *      input or in a toast.
 */
export function useCreateAgencyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateAgencyDto) => agenciesControllerCreateV1(dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
    },
  });
}

/**
 * Re-issues the root activation token and re-sends the email.
 *
 * The recovery path when an activation link expires (7 days) — there is nothing
 * the agency's root user can do from their side, so an admin has to trigger it.
 * Invalidates the detail view because the invite expiry moves.
 */
export function useResendRootInviteMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => agenciesControllerResendRootInviteV1(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agencyKeys.detail(id) });
    },
  });
}

/** Assign or switch a comped tier. Lists change too (tier column). */
export function useAssignSubscriptionMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AssignSubscriptionDto) => agenciesControllerAssignSubscriptionV1(id, dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agencyKeys.all });
    },
  });
}

/** Back to untiered: no limits, nothing to pay. */
export function useRemoveSubscriptionMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => agenciesControllerRemoveSubscriptionV1(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agencyKeys.all });
    },
  });
}
